import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVeloraStore } from '../store/useVeloraStore';
import { usePitch } from '../hooks/usePitch';
import { findVideoId, getLyrics, type LrcLine } from '../lib/songSearch';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: (() => void) | undefined;
  }
}

type Status = 'idle' | 'loaded' | 'playing' | 'paused';

interface LocationState {
  trackName?: string | null;
  trackId?: string;
  fromFile?: boolean;
  mode?: 'karaoke';
  artist?: string;
  title?: string;
}

const NOTE_MIDI: Record<string, number> = {};
(function buildNoteMap() {
  const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  for (let octave = 0; octave <= 8; octave++) {
    for (let i = 0; i < 12; i++) {
      NOTE_MIDI[`${names[i]}${octave}`] = (octave + 1) * 12 + i;
    }
  }
})();

function noteToY(note: string, H: number): number {
  const midi = NOTE_MIDI[note] ?? 60;
  return H - ((Math.max(36, Math.min(84, midi)) - 36) / 48) * H;
}
function freqToY(freq: number, H: number): number {
  if (freq <= 0) return H / 2;
  const midi = 12 * Math.log2(freq / 440) + 69;
  return H - ((Math.max(36, Math.min(84, midi)) - 36) / 48) * H;
}

const LINE_H = 32;
const LINES_VISIBLE = 6;
const CONTAINER_H = LINE_H * LINES_VISIBLE;
const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export default function Song() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const isKaraoke = state.mode === 'karaoke';
  const karaokeArtist = state.artist ?? '';
  const karaokeTitle = state.title ?? '';
  const trackName = state.trackName ?? null;
  const fromFile = state.fromFile ?? false;

  const {
    currentNote, currentFrequency, currentCents,
    transposeSemitones, setTranspose,
    score, inTuneStreak,
  } = useVeloraStore();
  const { startPitch, stopPitch } = usePitch();

  // ── state (all hooks before any early return) ──
  const [status, setStatus] = useState<Status>('idle');
  const [songName, setSongName] = useState(trackName ?? '');
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [videoError, setVideoError] = useState(false);
  const [lrcLines, setLrcLines] = useState<LrcLine[]>([]);
  const [ytTime, setYtTime] = useState(0);
  const [showVideo, setShowVideo] = useState(true);
  const [karaokeReady, setKaraokeReady] = useState(false);
  const [karaokeLoading, setKaraokeLoading] = useState(isKaraoke);

  // ── refs ──
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pitchTrailRef = useRef<{ y: number; cents: number }[]>([]);
  const floatingRef = useRef<{ text: string; y: number; opacity: number; id: number }[]>([]);
  const streakTriggeredRef = useRef(0);
  const rafRef = useRef<number>(0);
  const ytPlayerRef = useRef<any>(null);
  const audioEl = useRef<HTMLAudioElement | null>(null);
  const objectUrl = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const noteColor = currentNote === '--' ? '#534AB7'
    : Math.abs(currentCents) <= 10 ? '#4ade80'
    : Math.abs(currentCents) <= 25 ? '#facc15' : '#f87171';

  // ── effects ──

  useEffect(() => {
    if (inTuneStreak >= 5 && inTuneStreak !== streakTriggeredRef.current && inTuneStreak % 5 === 0) {
      streakTriggeredRef.current = inTuneStreak;
      const canvas = canvasRef.current;
      if (canvas) floatingRef.current.push({ text: '+Perfect!', y: canvas.height * 0.4, opacity: 1, id: Date.now() });
    }
  }, [inTuneStreak]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animId: number;
    const draw = () => {
      const W = canvas.width; const H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#100e30'; ctx.fillRect(0, 0, W, H);
      const names = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
      for (let oct = 2; oct <= 5; oct++) {
        for (const n of names) {
          const y = noteToY(`${n}${oct}`, H);
          ctx.beginPath();
          ctx.strokeStyle = n === 'C' ? 'rgba(127,119,221,0.25)' : 'rgba(127,119,221,0.08)';
          ctx.lineWidth = n === 'C' ? 1 : 0.5;
          ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
          if (n === 'C') {
            ctx.fillStyle = 'rgba(127,119,221,0.45)';
            ctx.font = '9px monospace';
            ctx.fillText(`C${oct}`, 4, y - 2);
          }
        }
      }
      if (currentFrequency > 0) {
        pitchTrailRef.current.push({ y: freqToY(currentFrequency, H), cents: currentCents });
        if (pitchTrailRef.current.length > 80) pitchTrailRef.current.shift();
      } else {
        if (pitchTrailRef.current.length > 0) pitchTrailRef.current.shift();
      }
      if (pitchTrailRef.current.length > 1) {
        const stepX = W / 80;
        ctx.beginPath(); ctx.lineWidth = 2.5;
        ctx.strokeStyle = noteColor + 'cc'; ctx.shadowBlur = 8; ctx.shadowColor = noteColor;
        pitchTrailRef.current.forEach((pt, i) => {
          const x = (W - pitchTrailRef.current.length * stepX) + i * stepX;
          if (i === 0) ctx.moveTo(x, pt.y); else ctx.lineTo(x, pt.y);
        });
        ctx.stroke(); ctx.shadowBlur = 0;
      }
      if (pitchTrailRef.current.length > 0) {
        const last = pitchTrailRef.current[pitchTrailRef.current.length - 1];
        ctx.beginPath(); ctx.arc(W - 12, last.y, 8, 0, Math.PI * 2);
        ctx.fillStyle = noteColor; ctx.shadowBlur = 16; ctx.shadowColor = noteColor; ctx.fill(); ctx.shadowBlur = 0;
        ctx.beginPath(); ctx.arc(W - 12, last.y, 4, 0, Math.PI * 2); ctx.fillStyle = 'white'; ctx.fill();
      }
      floatingRef.current = floatingRef.current.map(f => ({ ...f, y: f.y - 1, opacity: f.opacity - 0.015 })).filter(f => f.opacity > 0);
      for (const f of floatingRef.current) {
        ctx.globalAlpha = f.opacity; ctx.fillStyle = '#4ade80';
        ctx.font = 'bold 14px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(f.text, W / 2, f.y); ctx.globalAlpha = 1; ctx.textAlign = 'left';
      }
      animId = requestAnimationFrame(draw);
    };
    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, [currentFrequency, currentCents, noteColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    return () => {
      stopPitch();
      audioEl.current?.pause();
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [stopPitch]);

  // karaoke: load data
  useEffect(() => {
    if (!isKaraoke) return;
    Promise.all([
      findVideoId(karaokeArtist, karaokeTitle),
      getLyrics(karaokeArtist, karaokeTitle),
    ]).then(([vid, lrc]) => {
      if (!vid) setVideoError(true);
      setVideoId(vid);
      setLrcLines(lrc);
      setKaraokeLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // karaoke: init YouTube player
  useEffect(() => {
    if (!isKaraoke || !videoId) return;
    let destroyed = false;
    const createPlayer = () => {
      if (destroyed || !window.YT?.Player) return;
      ytPlayerRef.current = new window.YT.Player('yt-karaoke-player', {
        videoId,
        playerVars: { controls: 1, modestbranding: 1, rel: 0, playsinline: 1 },
        events: { onReady: () => { if (!destroyed) setKaraokeReady(true); } },
      });
    };
    if (window.YT?.Player) {
      createPlayer();
    } else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); createPlayer(); };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const s = document.createElement('script');
        s.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(s);
      }
    }
    return () => {
      destroyed = true;
      ytPlayerRef.current?.destroy?.();
      ytPlayerRef.current = null;
    };
  }, [isKaraoke, videoId]);

  // karaoke: poll YouTube time
  useEffect(() => {
    if (!karaokeReady) return;
    const id = setInterval(() => {
      const t = ytPlayerRef.current?.getCurrentTime?.() ?? 0;
      setYtTime(t);
    }, 200);
    return () => clearInterval(id);
  }, [karaokeReady]);

  // ── memos ──
  const activeLyricIdx = useMemo(() => {
    if (!lrcLines.length) return -1;
    for (let i = lrcLines.length - 1; i >= 0; i--) {
      if (ytTime >= lrcLines[i].time - 0.3) return i;
    }
    return 0;
  }, [lrcLines, ytTime]);

  const lyricsTranslateY = useMemo(() => {
    if (activeLyricIdx < 0) return 0;
    const center = activeLyricIdx * LINE_H - (CONTAINER_H / 2 - LINE_H / 2);
    const maxY = Math.max(0, lrcLines.length * LINE_H - CONTAINER_H);
    return Math.min(maxY, Math.max(0, center));
  }, [activeLyricIdx, lrcLines.length]);

  // ── callbacks ──
  const playNoAudio = useCallback(async () => {
    await startPitch();
    setStatus('playing');
  }, [startPitch]);

  const stopSinging = useCallback(() => {
    stopPitch();
    setStatus('idle');
    cancelAnimationFrame(rafRef.current);
  }, [stopPitch]);

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    const url = URL.createObjectURL(file);
    objectUrl.current = url;
    const audio = new Audio(url);
    audio.volume = volume;
    audio.onloadedmetadata = () => {
      setDuration(audio.duration);
      setStatus('loaded');
      setSongName(file.name.replace(/\.[^.]+$/, ''));
    };
    audioEl.current = audio;
  }, [volume]);

  const play = useCallback(async () => {
    await startPitch();
    if (audioEl.current) {
      audioEl.current.play();
      const tick = () => {
        setCurrentTime(audioEl.current?.currentTime ?? 0);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      audioEl.current.onended = () => {
        stopPitch(); setStatus('loaded'); setCurrentTime(0);
        cancelAnimationFrame(rafRef.current);
      };
    }
    setStatus('playing');
  }, [startPitch, stopPitch]);

  const pausePractice = useCallback(() => {
    audioEl.current?.pause();
    stopPitch();
    setStatus(audioEl.current ? 'paused' : 'idle');
    cancelAnimationFrame(rafRef.current);
  }, [stopPitch]);

  const resume = useCallback(async () => {
    await startPitch();
    if (audioEl.current) {
      audioEl.current.play();
      const tick = () => {
        setCurrentTime(audioEl.current?.currentTime ?? 0);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }
    setStatus('playing');
  }, [startPitch]);

  const seek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    if (audioEl.current) audioEl.current.currentTime = t;
    setCurrentTime(t);
  }, []);

  const changeVolume = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioEl.current) audioEl.current.volume = v;
  }, []);

  // ════════════════════════════════════════════
  //  KARAOKE MODE
  // ════════════════════════════════════════════
  if (isKaraoke) {
    return (
      <div className="flex flex-col min-h-screen pb-20" style={{ background: '#0d0b29' }}>
        {/* Header */}
        <div className="px-4 pt-12 pb-2 flex items-center gap-3">
          <button
            onClick={() => { stopPitch(); navigate('/songs'); }}
            className="p-2 rounded-xl transition-all active:scale-90"
            style={{ background: 'rgba(83,74,183,0.15)', border: '1px solid rgba(83,74,183,0.3)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="#7F77DD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-base font-semibold truncate" style={{ color: '#EEEDFE' }}>{karaokeTitle}</h1>
            <p className="text-xs truncate" style={{ color: '#AFA9EC' }}>{karaokeArtist}</p>
          </div>
          {!karaokeLoading && !videoError && (
            <button
              onClick={() => setShowVideo(v => !v)}
              className="p-2 rounded-xl transition-all active:scale-90"
              style={{
                background: showVideo ? 'rgba(83,74,183,0.3)' : 'rgba(83,74,183,0.1)',
                border: `1px solid ${showVideo ? '#7F77DD' : 'rgba(83,74,183,0.3)'}`,
              }}
            >
              {showVideo ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="5" width="15" height="14" rx="2" stroke="#9B94E8" strokeWidth="1.8"/>
                  <path d="m17 9 5-3v12l-5-3" stroke="#9B94E8" strokeWidth="1.8" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18V5l12-2v13" stroke="#534AB7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="6" cy="18" r="3" stroke="#534AB7" strokeWidth="1.8"/>
                  <circle cx="18" cy="16" r="3" stroke="#534AB7" strokeWidth="1.8"/>
                </svg>
              )}
            </button>
          )}
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold font-mono" style={{ color: '#9B94E8' }}>{score}</div>
            <div className="text-xs" style={{ color: '#534AB7' }}>score</div>
          </div>
        </div>

        {/* YouTube player */}
        {karaokeLoading ? (
          <div className="mx-4 mb-3 rounded-2xl flex items-center justify-center gap-2"
            style={{ height: 160, background: 'rgba(16,14,48,0.9)', border: '1px solid rgba(83,74,183,0.3)' }}>
            <div className="w-4 h-4 rounded-full border-2 animate-spin"
              style={{ borderColor: 'rgba(83,74,183,0.3)', borderTopColor: '#7F77DD' }} />
            <span className="text-xs" style={{ color: '#AFA9EC' }}>Loading…</span>
          </div>
        ) : videoError ? (
          <div className="mx-4 mb-2 rounded-2xl flex items-center justify-center px-4"
            style={{ height: 48, background: 'rgba(16,14,48,0.7)', border: '1px solid rgba(83,74,183,0.15)' }}>
            <p className="text-xs text-center" style={{ color: '#AFA9EC' }}>Video not found — sing along with the lyrics</p>
          </div>
        ) : (
          <div
            className="mx-4 mb-3 rounded-2xl overflow-hidden transition-all duration-300"
            style={{
              height: showVideo ? 180 : 0,
              opacity: showVideo ? 1 : 0,
              border: showVideo ? '1px solid rgba(83,74,183,0.3)' : 'none',
            }}
          >
            <div id="yt-karaoke-player" style={{ width: '100%', height: 180 }} />
          </div>
        )}

        {/* Pitch canvas */}
        <div className="px-4 mb-2">
          <canvas
            ref={canvasRef}
            style={{
              width: '100%',
              height: (!karaokeLoading && (videoError || !showVideo)) ? 140 : 100,
              display: 'block',
              borderRadius: 12,
              border: '1px solid rgba(83,74,183,0.3)',
            }}
          />
        </div>

        {/* Note + Sing button */}
        <div className="px-4 mb-3 flex items-center gap-3">
          <div className="text-3xl font-display font-bold transition-all duration-75"
            style={{ color: noteColor, minWidth: 54 }}>
            {currentNote}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs" style={{ color: '#534AB7' }}>
              {currentFrequency > 0 ? `${Math.round(currentFrequency)} Hz` : 'No signal'}
            </div>
            <div className="text-xs font-medium mt-0.5" style={{ color: noteColor }}>
              {currentNote !== '--'
                ? Math.abs(currentCents) <= 10 ? '✓ In tune'
                  : currentCents > 0 ? `+${currentCents}¢ sharp` : `${currentCents}¢ flat`
                : status === 'playing' ? 'Sing…' : 'Ready'}
            </div>
          </div>
          {inTuneStreak >= 3 && (
            <div className="px-2 py-1 rounded-lg text-xs font-bold"
              style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)' }}>
              ×{inTuneStreak}
            </div>
          )}
          <button
            onClick={status === 'playing' ? stopSinging : playNoAudio}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all active:scale-95"
            style={{
              background: status === 'playing' ? 'rgba(248,113,113,0.15)' : 'linear-gradient(135deg,#534AB7,#7F77DD)',
              color: status === 'playing' ? '#f87171' : 'white',
              border: status === 'playing' ? '1px solid rgba(248,113,113,0.3)' : 'none',
              boxShadow: status !== 'playing' ? '0 2px 12px rgba(83,74,183,0.4)' : 'none',
            }}
          >
            {status === 'playing' ? 'Stop' : 'Sing'}
          </button>
        </div>

        {/* Lyrics */}
        {!karaokeLoading && (
          lrcLines.length > 0 ? (
            <div className="px-4">
              <div
                style={{
                  height: CONTAINER_H,
                  overflow: 'hidden',
                  maskImage: 'linear-gradient(transparent 0%, black 18%, black 82%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(transparent 0%, black 18%, black 82%, transparent 100%)',
                }}
              >
                <div
                  style={{
                    transform: `translateY(-${lyricsTranslateY}px)`,
                    transition: 'transform 0.3s ease',
                    paddingTop: CONTAINER_H / 2 - LINE_H / 2,
                    paddingBottom: CONTAINER_H / 2 - LINE_H / 2,
                  }}
                >
                  {lrcLines.map((line, i) => {
                    const isActive = i === activeLyricIdx;
                    const dist = Math.abs(i - activeLyricIdx);
                    return (
                      <div
                        key={i}
                        style={{
                          height: LINE_H,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          paddingLeft: 8,
                          paddingRight: 8,
                          color: isActive ? '#EEEDFE' : '#AFA9EC',
                          fontWeight: isActive ? '700' : '400',
                          fontSize: isActive ? 16 : 13,
                          opacity: isActive ? 1 : dist === 1 ? 0.55 : dist === 2 ? 0.3 : 0.15,
                          textShadow: isActive ? '0 0 14px rgba(159,148,232,0.7)' : 'none',
                          transition: 'all 0.25s ease',
                        }}
                      >
                        {line.text || '♩'}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 text-center py-6">
              <p className="text-sm" style={{ color: '#534AB7' }}>No synced lyrics found</p>
              <p className="text-xs mt-1" style={{ color: '#AFA9EC' }}>Sing along with the video</p>
            </div>
          )
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════
  //  PRACTICE / FILE MODE
  // ════════════════════════════════════════════
  const feedbackLabel = currentNote === '--' ? null
    : Math.abs(currentCents) <= 10 ? 'Perfect! 🎯'
    : Math.abs(currentCents) <= 25 ? 'Good!' : 'Off';
  const feedbackColor = currentNote === '--' ? '#534AB7'
    : Math.abs(currentCents) <= 10 ? '#4ade80'
    : Math.abs(currentCents) <= 25 ? '#facc15' : '#f87171';
  const isPracticeTrack = !fromFile && trackName !== null;
  const hasAudio = !!audioEl.current;

  const handlePlayPause = () => {
    if (status === 'playing') pausePractice();
    else if (status === 'paused') resume();
    else if (hasAudio) play();
    else if (isPracticeTrack) playNoAudio();
    else fileInput.current?.click();
  };

  return (
    <div className="flex flex-col min-h-screen pb-20" style={{ background: '#100e30' }}>
      <div className="px-4 pt-12 pb-3 flex items-center gap-3">
        <button
          onClick={() => { stopPitch(); audioEl.current?.pause(); navigate('/songs'); }}
          className="p-2 rounded-xl transition-all active:scale-90"
          style={{ background: 'rgba(83,74,183,0.15)', border: '1px solid rgba(83,74,183,0.3)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="#7F77DD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-base font-semibold truncate" style={{ color: '#EEEDFE' }}>
            {songName || 'My Song'}
          </h1>
          {status === 'playing' && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#f87171' }} />
              <span className="text-xs" style={{ color: '#AFA9EC' }}>Live</span>
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-bold font-mono" style={{ color: '#9B94E8' }}>{score}</div>
          <div className="text-xs" style={{ color: '#534AB7' }}>score</div>
        </div>
      </div>

      <div className="px-4 mb-3">
        <div style={{ width: '100%', height: 200, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(83,74,183,0.3)', position: 'relative' }}>
          <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
          {feedbackLabel && (
            <div
              className="absolute top-2 right-3 text-sm font-bold px-2 py-0.5 rounded-lg"
              style={{ color: feedbackColor, background: `${feedbackColor}18`, border: `1px solid ${feedbackColor}40`, transition: 'color 0.15s, background 0.15s' }}
            >
              {feedbackLabel}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 mb-3 flex items-center justify-between gap-3">
        <div className="text-4xl font-display font-bold transition-all duration-75"
          style={{ color: noteColor, textShadow: currentNote !== '--' ? `0 0 20px ${noteColor}60` : 'none', minWidth: 64 }}>
          {currentNote}
        </div>
        <div className="flex-1 text-right">
          <div className="text-xs" style={{ color: '#534AB7' }}>
            {currentFrequency > 0 ? `${Math.round(currentFrequency)} Hz` : 'No signal'}
          </div>
          <div className="text-xs font-medium mt-0.5" style={{ color: noteColor }}>
            {currentNote !== '--'
              ? Math.abs(currentCents) <= 10 ? '✓ In tune'
                : currentCents > 0 ? `+${currentCents}¢ sharp` : `${currentCents}¢ flat`
              : status === 'playing' ? 'Sing into microphone…' : 'Ready'}
          </div>
        </div>
        {inTuneStreak >= 3 && (
          <div className="px-2 py-1 rounded-lg text-xs font-bold"
            style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)' }}>
            ×{inTuneStreak}
          </div>
        )}
      </div>

      <div className="px-4 flex flex-col gap-3">
        <div className="rounded-2xl p-4" style={{ background: 'rgba(10,8,32,0.8)', border: '1px solid rgba(83,74,183,0.3)' }}>
          {hasAudio && (
            <>
              <input type="range" min={0} max={duration || 1} step={0.1} value={currentTime} onChange={seek} className="w-full mb-1 accent-purple-500"/>
              <div className="flex justify-between text-xs mb-3" style={{ color: '#534AB7' }}>
                <span>{fmt(currentTime)}</span><span>{fmt(duration)}</span>
              </div>
            </>
          )}
          <div className="flex items-center justify-center mb-3">
            <button
              onClick={handlePlayPause}
              className="w-16 h-16 rounded-full flex items-center justify-center transition-all active:scale-95"
              style={{ background: 'linear-gradient(135deg,#534AB7,#7F77DD)', boxShadow: '0 4px 24px rgba(83,74,183,0.55)' }}
            >
              {status === 'playing'
                ? <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                : <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><polygon points="6,3 20,12 6,21"/></svg>
              }
            </button>
          </div>
          {hasAudio && (
            <div className="flex items-center gap-3 mb-3">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M12 2a1 1 0 0 0-1 1v1a1 1 0 0 0 2 0V3a1 1 0 0 0-1-1z" fill="#534AB7"/>
                <rect x="9" y="4" width="6" height="11" rx="3" stroke="#534AB7" strokeWidth="1.8"/>
                <path d="M5 10a7 7 0 0 0 14 0" stroke="#534AB7" strokeWidth="1.8" strokeLinecap="round"/>
                <line x1="12" y1="17" x2="12" y2="21" stroke="#534AB7" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              <input type="range" min={0} max={1} step={0.05} value={volume} onChange={changeVolume} className="flex-1 accent-purple-500"/>
              <span className="text-xs font-mono w-8 text-right" style={{ color: '#AFA9EC' }}>{Math.round(volume * 100)}%</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: '#AFA9EC' }}>Transpose</span>
            <button onClick={() => setTranspose(transposeSemitones - 1)} className="w-8 h-8 rounded-lg flex items-center justify-center font-bold transition-all active:scale-90" style={{ background: 'rgba(83,74,183,0.2)', color: '#7F77DD', border: '1px solid rgba(83,74,183,0.3)' }}>−</button>
            <span className="w-12 text-center text-sm font-mono font-semibold" style={{ color: transposeSemitones === 0 ? '#AFA9EC' : '#9B94E8' }}>
              {transposeSemitones === 0 ? '0st' : transposeSemitones > 0 ? `+${transposeSemitones}st` : `${transposeSemitones}st`}
            </span>
            <button onClick={() => setTranspose(transposeSemitones + 1)} className="w-8 h-8 rounded-lg flex items-center justify-center font-bold transition-all active:scale-90" style={{ background: 'rgba(83,74,183,0.2)', color: '#7F77DD', border: '1px solid rgba(83,74,183,0.3)' }}>+</button>
            <div className="flex-1" />
            {(fromFile || (!isPracticeTrack && !hasAudio)) && (
              <button onClick={() => fileInput.current?.click()} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all active:scale-95" style={{ background: 'rgba(83,74,183,0.15)', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.3)' }}>
                {songName ? songName.slice(0, 14) + (songName.length > 14 ? '…' : '') : 'Choose File'}
              </button>
            )}
          </div>
        </div>
      </div>

      <input ref={fileInput} type="file" accept="audio/*" className="hidden" onChange={onFileChange}/>
    </div>
  );
}
